/**
 * `docs/KNOWN-BUGS.md` item 4: "A resize handler that unconditionally resizes
 * the canvas can loop against a mobile browser's sliding URL bar. Recompute
 * the cell size, and return without touching the canvas if it is unchanged."
 *
 * `computeCellSize` is a pure function: container box in, whole-pixel cell
 * geometry out. `ResizeGuard` wraps it with the one bit of memory the bug
 * needs — "did this actually change" — so `Canvas2DRenderer.resize` (the only
 * thing here that touches a real `HTMLCanvasElement`) can early-return
 * without writing to `canvas.width`/`canvas.height`/`canvas.style` when it
 * did not.
 */

export interface CellSizeInput {
  readonly containerWidth: number;
  readonly containerHeight: number;
  /** Playfield columns — `board.width`. */
  readonly cols: number;
  /** Visible rows — `board.visibleRows`. */
  readonly rows: number;
  readonly devicePixelRatio: number;
  /** Extra width reserved for hold/next rails, in cell units. Default 0. */
  readonly railColumns?: number;
}

export interface CellSize {
  /** Whole CSS pixels. Draw coordinates snap to this grid. */
  readonly cellPx: number;
  readonly widthPx: number;
  readonly heightPx: number;
  /** Device pixels — what `canvas.width`/`canvas.height` should be set to. */
  readonly bufferWidthPx: number;
  readonly bufferHeightPx: number;
  readonly devicePixelRatio: number;
}

const MIN_CELL_PX = 8;
const MAX_CELL_PX = 64;

export function computeCellSize(input: CellSizeInput): CellSize {
  const railColumns = input.railColumns ?? 0;
  const totalCols = Math.max(1, input.cols + railColumns);
  const rows = Math.max(1, input.rows);

  const byWidth = Math.floor(input.containerWidth / totalCols);
  const byHeight = Math.floor(input.containerHeight / rows);
  const cellPx = Math.max(MIN_CELL_PX, Math.min(MAX_CELL_PX, byWidth, byHeight));

  const widthPx = cellPx * totalCols;
  const heightPx = cellPx * rows;
  const dpr = input.devicePixelRatio > 0 ? input.devicePixelRatio : 1;

  return {
    cellPx,
    widthPx,
    heightPx,
    bufferWidthPx: Math.round(widthPx * dpr),
    bufferHeightPx: Math.round(heightPx * dpr),
    devicePixelRatio: dpr,
  };
}

export function sameCellSize(a: CellSize | null, b: CellSize): boolean {
  if (a === null) return false;
  return (
    a.cellPx === b.cellPx &&
    a.widthPx === b.widthPx &&
    a.heightPx === b.heightPx &&
    a.bufferWidthPx === b.bufferWidthPx &&
    a.bufferHeightPx === b.bufferHeightPx &&
    a.devicePixelRatio === b.devicePixelRatio
  );
}

/** The guard itself: remembers the last applied size, and reports only real changes. */
export class ResizeGuard {
  private current: CellSize | null = null;

  /** The last size `apply` returned, or `null` before the first call. */
  get size(): CellSize | null {
    return this.current;
  }

  /**
   * Compute the size for `input`. Returns the new `CellSize` when it differs
   * from the last one applied, or `null` when nothing changed — the signal
   * the caller uses to skip touching the canvas entirely.
   */
  apply(input: CellSizeInput): CellSize | null {
    const next = computeCellSize(input);
    if (sameCellSize(this.current, next)) return null;
    this.current = next;
    return next;
  }
}
